import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { trpc } from "@/lib/trpc";
import { Beaker, Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function WellsManagement() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWell, setEditingWell] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [wellName, setWellName] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [chlorineGallonsPerInch, setChlorineGallonsPerInch] = useState("");
  const [meterMultiplier, setMeterMultiplier] = useState("1");
  const [normalMinGallons, setNormalMinGallons] = useState("");
  const [normalMaxGallons, setNormalMaxGallons] = useState("");
  const [hasHourMeter, setHasHourMeter] = useState(true);
  const [hasChlorineGauge, setHasChlorineGauge] = useState(true);
  const [hasTowerResidual, setHasTowerResidual] = useState(true);
  const [requireMeterPhoto, setRequireMeterPhoto] = useState(false);
  const [requireChlorinePhoto, setRequireChlorinePhoto] = useState(false);
  const [requireResidualPhoto, setRequireResidualPhoto] = useState(false);
  const [isActive, setIsActive] = useState(true);
  
  // Field configuration state
  const [fieldConfig, setFieldConfig] = useState({
    waterProduction: { enabled: true, required: false },
    chlorine: { enabled: true, required: false },
    pressures: { enabled: true, required: false },
    residuals: { enabled: true, required: false },
    hourMeter: { enabled: true, required: false },
    photos: { enabled: true, required: false },
  });

  const { data: wells, refetch } = trpc.wells.list.useQuery({});
  const createMutation = trpc.wells.create.useMutation();
  const updateMutation = trpc.wells.update.useMutation();
  const deleteMutation = trpc.wells.delete.useMutation();

  const resetForm = () => {
    setWellName("");
    setLocation("");
    setDescription("");
    setChlorineGallonsPerInch("");
    setMeterMultiplier("1");
    setNormalMinGallons("");
    setNormalMaxGallons("");
    setHasHourMeter(true);
    setHasChlorineGauge(true);
    setHasTowerResidual(true);
    setRequireMeterPhoto(false);
    setRequireChlorinePhoto(false);
    setRequireResidualPhoto(false);
    setIsActive(true);
    setFieldConfig({
      waterProduction: { enabled: true, required: false },
      chlorine: { enabled: true, required: false },
      pressures: { enabled: true, required: false },
      residuals: { enabled: true, required: false },
      hourMeter: { enabled: true, required: false },
      photos: { enabled: true, required: false },
    });
    setEditingWell(null);
  };

  const openDialog = (well?: any) => {
    if (well) {
      setEditingWell(well);
      setWellName(well.wellName);
      setLocation(well.location || "");
      setDescription(well.description || "");
      setChlorineGallonsPerInch(well.chlorineGallonsPerInch || "");
      setMeterMultiplier(well.meterMultiplier || "1");
      setNormalMinGallons(well.normalMinGallons || "");
      setNormalMaxGallons(well.normalMaxGallons || "");
      setHasHourMeter(well.hasHourMeter ?? true);
      setHasChlorineGauge(well.hasChlorineGauge ?? true);
      setHasTowerResidual(well.hasTowerResidual ?? true);
      setRequireMeterPhoto(well.requireMeterPhoto ?? false);
      setRequireChlorinePhoto(well.requireChlorinePhoto ?? false);
      setRequireResidualPhoto(well.requireResidualPhoto ?? false);
      setIsActive(well.isActive);
      setFieldConfig(well.fieldConfig || {
        waterProduction: { enabled: true, required: false },
        chlorine: { enabled: true, required: false },
        pressures: { enabled: true, required: false },
        residuals: { enabled: true, required: false },
        hourMeter: { enabled: true, required: false },
        photos: { enabled: true, required: false },
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!wellName.trim()) {
      toast.error("Well name is required");
      return;
    }

    try {
      const data = {
        wellName,
        location,
        description,
        chlorineGallonsPerInch,
        meterMultiplier,
        normalMinGallons,
        normalMaxGallons,
        hasHourMeter,
        hasChlorineGauge,
        hasTowerResidual,
        requireMeterPhoto,
        requireChlorinePhoto,
        requireResidualPhoto,
        isActive,
        fieldConfig,
      };

      if (editingWell) {
        await updateMutation.mutateAsync({ id: editingWell.id, ...data });
        toast.success("Well updated successfully");
      } else {
        await createMutation.mutateAsync(data);
        toast.success("Well created successfully");
      }

      refetch();
      setIsDialogOpen(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Failed to save well");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteMutation.mutateAsync({ id: deleteId });
      toast.success("Well deleted successfully");
      refetch();
      setDeleteId(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete well");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                ← Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold">Wells Management</h1>
            </div>
            <Button onClick={() => openDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              New Well
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6">
        <div className="space-y-4">
          {wells?.map((well) => (
            <Card key={well.id}>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{well.wellName}</h3>
                      {!well.isActive && (
                        <span className="text-xs bg-muted px-2 py-1 rounded">Inactive</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {well.location && <p>Location: {well.location}</p>}
                      {well.description && <p>Description: {well.description}</p>}
                      {well.chlorineGallonsPerInch && (
                        <p>Chlorine Gal/Inch: {well.chlorineGallonsPerInch}</p>
                      )}
                      {well.meterMultiplier && <p>Meter Multiplier: {well.meterMultiplier}</p>}
                      {well.normalMinGallons && well.normalMaxGallons && (
                        <p>
                          Normal Range: {well.normalMinGallons} - {well.normalMaxGallons} gallons
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/wells/${well.id}/chlorine-config`)}>
                      <Beaker className="w-4 h-4 mr-2" />
                      Chlorine Config
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDialog(well)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteId(well.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                </div>
              </CardContent>
            </Card>
          ))}

          {wells && wells.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No wells configured</p>
                <Button className="mt-4" onClick={() => openDialog()}>
                  Create Your First Well
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWell ? "Edit Well" : "Create New Well"}</DialogTitle>
            <DialogDescription>
              Configure well parameters and thresholds
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Well Name *</Label>
              <Input
                id="name"
                value={wellName}
                onChange={(e) => setWellName(e.target.value)}
                placeholder="e.g., Well 14"
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., North Station"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="chlorine">Chlorine Gallons per Inch</Label>
                <Input
                  id="chlorine"
                  type="number"
                  step="0.01"
                  value={chlorineGallonsPerInch}
                  onChange={(e) => setChlorineGallonsPerInch(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="multiplier">Meter Multiplier</Label>
                <Input
                  id="multiplier"
                  type="number"
                  step="0.01"
                  value={meterMultiplier}
                  onChange={(e) => setMeterMultiplier(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min">Normal Min Gallons</Label>
                <Input
                  id="min"
                  type="number"
                  step="0.01"
                  value={normalMinGallons}
                  onChange={(e) => setNormalMinGallons(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="max">Normal Max Gallons</Label>
                <Input
                  id="max"
                  type="number"
                  step="0.01"
                  value={normalMaxGallons}
                  onChange={(e) => setNormalMaxGallons(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Equipment Configuration</Label>
              <p className="text-sm text-muted-foreground">Select which equipment is available at this well</p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="hourMeter"
                  checked={hasHourMeter}
                  onCheckedChange={setHasHourMeter}
                />
                <Label htmlFor="hourMeter">Has Hour Meter</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="chlorineGauge"
                  checked={hasChlorineGauge}
                  onCheckedChange={setHasChlorineGauge}
                />
                <Label htmlFor="chlorineGauge">Has Chlorine Gauge</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="towerResidual"
                  checked={hasTowerResidual}
                  onCheckedChange={setHasTowerResidual}
                />
                <Label htmlFor="towerResidual">Has Tower Residual Measurement</Label>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Field Configuration</Label>
              <p className="text-sm text-muted-foreground">Control which fields appear on the Daily Log Entry page for this well. Remarks are always shown and optional.</p>
              
              {[
                { key: 'waterProduction', label: 'Water Production' },
                { key: 'chlorine', label: 'Chlorine/Chemicals' },
                { key: 'pressures', label: 'Pressures' },
                { key: 'residuals', label: 'Residuals' },
                { key: 'hourMeter', label: 'Hour Meter' },
                { key: 'photos', label: 'Field Photos' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <Label className="font-medium">{label}</Label>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`${key}-enabled`}
                        checked={fieldConfig[key as keyof typeof fieldConfig]?.enabled ?? true}
                        onCheckedChange={(checked) => setFieldConfig(prev => ({
                          ...prev,
                          [key]: { ...prev[key as keyof typeof prev], enabled: checked }
                        }))}
                      />
                      <Label htmlFor={`${key}-enabled`} className="text-sm">Enabled</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`${key}-required`}
                        checked={fieldConfig[key as keyof typeof fieldConfig]?.required ?? false}
                        disabled={!fieldConfig[key as keyof typeof fieldConfig]?.enabled}
                        onCheckedChange={(checked) => setFieldConfig(prev => ({
                          ...prev,
                          [key]: { ...prev[key as keyof typeof prev], required: checked }
                        }))}
                      />
                      <Label htmlFor={`${key}-required`} className="text-sm">Required</Label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Photo Requirements</Label>
              <p className="text-sm text-muted-foreground">Require photos for specific instruments before log submission</p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requireMeterPhoto"
                  checked={requireMeterPhoto}
                  onCheckedChange={setRequireMeterPhoto}
                />
                <Label htmlFor="requireMeterPhoto">Require Meter Reading Photo</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="requireChlorinePhoto"
                  checked={requireChlorinePhoto}
                  onCheckedChange={setRequireChlorinePhoto}
                />
                <Label htmlFor="requireChlorinePhoto">Require Chlorine Gauge Photo</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="requireResidualPhoto"
                  checked={requireResidualPhoto}
                  onCheckedChange={setRequireResidualPhoto}
                />
                <Label htmlFor="requireResidualPhoto">Require Residual Measurement Photo</Label>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingWell ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Well</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this well? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
